import { useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  LIVE_RELEASE_CHANNEL_NAME,
  Experience as ReleaseChannelExperience,
  useAllExperiences,
} from "../../../hooks/useAllExperiences";
import { useCompaniesData } from "../../../hooks/useCompaniesData";
import { useLocalization } from "../../../hooks/useLocalization";
import type { TExperience } from "../types";
import { IncomingLicense, useIncomingLicenses } from "./useIncomingLicenses";

// Logic to process a single Release Channel
const mapReleaseChannelToExperience = (
  rc: ReleaseChannelExperience,
  userCompanyId: string | null,
  licenses: Map<string, IncomingLicense>,
): TExperience | null => {
  const uniqueKey = rc.releaseChannelId;
  if (!uniqueKey) return null;

  const isInternal = Boolean(
    rc.companyId && userCompanyId && rc.companyId === userCompanyId,
  );

  const isGrantedExternal = Boolean(
    rc.viewerCompanyId && userCompanyId && rc.viewerCompanyId === userCompanyId,
  );

  const license = licenses.get(rc.releaseChannelId);
  const hasLicense = Boolean(license);
  const hasOwnershipData = Boolean(rc.companyId || rc.viewerCompanyId);

  // Filter Logic:
  // If we have ownership data, strictly check permissions
  if (hasOwnershipData) {
    if (!isInternal && !isGrantedExternal && !hasLicense) {
      return null;
    }
  }
  // If data is loading/missing, fall back to license check
  else if (!hasLicense) {
    return null;
  }

  return {
    id: rc.releaseChannelId,
    name: rc.friendlyName || rc.releaseChannelName,
    companyId: rc.companyId || undefined,
    companyName: rc.companyName ?? "—",
    isInternal,
    seats: license
      ? { used: license.allocatedSeats, total: license.seats }
      : { used: rc.seatsUsed ?? 0, total: null },
    expiresAt: license?.expiresAt ?? null,
  };
};

// Logic to create a License-Only Experience
const createLicensedExperience = (
  releaseChannelId: string,
  license: IncomingLicense,
  companyNamesByIdMap: Record<string, string>,
  fallbackName: string,
): TExperience => {
  const companyName =
    companyNamesByIdMap[license.issuingOrganizationId] ?? "External";

  return {
    id: releaseChannelId,
    name: license.licenseName || fallbackName,
    companyId: license.issuingOrganizationId,
    companyName,
    isInternal: false,
    seats: {
      used: license.allocatedSeats,
      total: license.seats,
    },
    expiresAt: license.expiresAt,
  };
};

const addStandardExperiences = (
  map: Map<string, TExperience>,
  list: ReleaseChannelExperience[],
  userCompanyId: string | null,
  licenses: Map<string, IncomingLicense>,
) => {
  for (const rc of list) {
    const experience = mapReleaseChannelToExperience(
      rc,
      userCompanyId,
      licenses,
    );

    if (experience && !map.has(experience.id)) {
      map.set(experience.id, experience);
    }
  }
};

const addFallbackLicenses = (
  map: Map<string, TExperience>,
  licenses: Map<string, IncomingLicense>,
  companyNamesByIdMap: Record<string, string>,
  fallbackName: string,
) => {
  const now = new Date();

  for (const [id, license] of licenses) {
    // Skip expired licenses
    if (license.expiresAt && license.expiresAt < now) {
      continue;
    }

    if (!map.has(id)) {
      const experience = createLicensedExperience(
        id,
        license,
        companyNamesByIdMap,
        fallbackName,
      );
      map.set(id, experience);
    }
  }
};

export const useExperiences = (
  enabled: boolean,
): {
  experiences: TExperience[];
  loading: boolean;
} => {
  const { t } = useLocalization();
  const { companyId } = useParams();

  const { experiences: rcExperiences, isLoading } = useAllExperiences({
    releaseChannelName: LIVE_RELEASE_CHANNEL_NAME,
    enabled: enabled,
    pageSize: 1000,
  });

  // Load licenses granted to our company
  const { licensesByReleaseChannel, isLoading: isLicensesLoading } =
    useIncomingLicenses();

  // Load company names for resolving issuing organization names
  const { companyNamesByIdMap, isLoading: isCompaniesLoading } =
    useCompaniesData();

  const fallbackName = t("experiences.licensedExperience");

  const experiences = useMemo<TExperience[]>(() => {
    const byReleaseChannelId = new Map<string, TExperience>();

    // Process standard Release Channels
    addStandardExperiences(
      byReleaseChannelId,
      rcExperiences,
      companyId!,
      licensesByReleaseChannel,
    );

    // Add License-Only Experiences
    if (rcExperiences.length > 0) {
      addFallbackLicenses(
        byReleaseChannelId,
        licensesByReleaseChannel,
        companyNamesByIdMap,
        fallbackName,
      );
    }

    return Array.from(byReleaseChannelId.values());
  }, [
    rcExperiences,
    companyId,
    licensesByReleaseChannel,
    companyNamesByIdMap,
    fallbackName,
  ]);

  return {
    experiences,
    loading: isLoading || isLicensesLoading || isCompaniesLoading,
  };
};
